import { Spinner } from '@components/gassan-ui'
import { useGigya } from '@lib/cdc/use-gigya'
import { createLogger } from '@lib/create-logger'
import { useRouter } from 'next/router'
import { useEffect, useRef } from 'react'
import { cn } from '@lib/cn'

// In CDC there are different screens (startScreens) depending on the flow (screenSet)
type GigyaStartScreen =
  | 'gigya-register-screen-form-only'
  | 'gigya-forgot-password-screen'
  | 'gigya-reset-password-screen'
  | 'gigya-social-login-buttons'
  | 'gigya-complete-registration-screen'
  | 'gigya-verification-pending-screen'

type GigyaScreenSetGassanLiteRegistration = {
  screenSet: 'Gassan-LiteRegistration'
  startScreen?: never
}
type GigyaScreenSetGassanRegistrationLogin = {
  screenSet: 'Gassan-RegistrationLogin'
  startScreen?: GigyaStartScreen
}

type GigyaScreenSetProps = (
  | GigyaScreenSetGassanLiteRegistration
  | GigyaScreenSetGassanRegistrationLogin
) & {
  children?: never
}

const logger = createLogger('GigyaScreenSet')

export function GigyaScreenSet({ screenSet, startScreen }: GigyaScreenSetProps) {
  const { isReady } = useGigya()
  const { locale, reload } = useRouter()
  const latestLocale = useRef(locale)

  const id = `${screenSet}-${startScreen}`.toLocaleLowerCase()
  useEffect(() => {
    if (isReady) {
      logger.info(`showing screen set ${screenSet}`)
      window?.gigya?.accounts.showScreenSet({
        screenSet: screenSet,
        startScreen: startScreen,
        containerID: id,
      })
    }
  }, [isReady, screenSet, startScreen, id])

  useEffect(() => {
    // We're using next/script to load the gigya script in the right locale
    // but nextjs won't reload the script if the locale changes
    // to keep the advantages of next/script we reload the page if the locale changes and we're displaying a Gigya screenset
    if (isReady && latestLocale.current !== locale) {
      reload()
    }

    latestLocale.current = locale
  }, [locale, reload, latestLocale, isReady])

  return (
    <>
      <div
        className={cn(
          /*
           ** Good luck.
           */
          // Base styles - h1
          '[&_h1]:mb-2 [&_h1]:border-b-0 [&_h1]:p-0 [&_h1]:text-[1.5rem]',

          // Select styles
          '[&_select]:-webkit-appearance-none [&_select]:-moz-appearance-none [&_select]:w-full [&_select]:appearance-none [&_select]:bg-[#fafafa] [&_select]:pl-3 [&_select]:pr-3',
          '[&_select]:bg-[url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAB4AAAAUCAMAAACtdX32AAAAdVBMVEUAAAD///8AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAhMdQaAAAAJ3RSTlMAAAECAwQGBwsOFBwkJTg5RUZ4eYCHkJefpaytrsXGy8zW3+Do8vNn0bsyAAAAYElEQVR42tXROwJDQAAA0Ymw1p9kiT+L5P5HVEi3qJn2lcPjtIuzUIJ/rhIGy762N3XaThqMN1ZPALsZPEzG1x8LrFL77DHBnEMxBewz0fJ6LyFHTPL7xhwzWYrJ9z22AqmQBV757MHfAAAAAElFTkSuQmCC)]',
          '[&_select]:bg-[length:1.5rem] [&_select]:bg-right [&_select]:bg-no-repeat',
          '[&_select:focus]:border-[#3e3e3e] [&_select:focus]:shadow-[0_0_0_3px_rgba(94,76,70,0.2)]',

          // Checkmark styles
          "[&_.gigya-show-checkmark]:bg-[url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M15 20.3636L18.3333 24C18.3333 24 19.5 22 21 20C22.5 18 25 16 25 16' stroke='%234CAF50'%0AstrokeWidth='2' strokeLinejoin='round' fill='none' /%3E%3C/svg%3E\")]",
          '[&_.gigya-show-checkmark]:bg-[length:2rem_2rem] [&_.gigya-show-checkmark]:bg-[calc(100%-0.5rem)_60%] [&_.gigya-show-checkmark]:bg-no-repeat [&_.gigya-show-checkmark]:pr-8',
          "sm:[&_.gigya-show-checkmark]:bg-[url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 40 40'%3E%3Cpath d='M15 20.3636L18.3333 24C18.3333 24 19.5 22 21 20C22.5 18 25 16 25 16' stroke='%234CAF50' stroke-width='2' stroke-linejoin='round' fill='none' /%3E%3C/svg%3E\")]",
          'sm:[&_.gigya-show-checkmark]:bg-[length:2.5rem_2.5rem] sm:[&_.gigya-show-checkmark]:bg-[calc(100%+0.25rem)_80%] sm:[&_.gigya-show-checkmark]:bg-no-repeat',

          // Input styles
          '[&_.gigya-composite-control_select]:h-12 [&_.gigya-composite-control_select]:rounded-none [&_.gigya-composite-control_select]:border [&_.gigya-composite-control_select]:border-[#c5c5c5] [&_.gigya-composite-control_select]:font-[Graphik,Arial,sans-serif] [&_.gigya-composite-control_select]:text-base [&_.gigya-composite-control_select]:transition-[border] [&_.gigya-composite-control_select]:duration-200',
          '[&_input]:h-12 [&_input]:rounded-none [&_input]:border [&_input]:border-[#c5c5c5] [&_input]:font-[Graphik,Arial,sans-serif] [&_input]:text-base [&_input]:transition-[border] [&_input]:duration-200',
          '[&_input::placeholder]:text-[#8f8f8f]',
          '[&_input:focus]:border-[#3e3e3e] [&_input:focus]:shadow-[0_0_0_3px_rgba(94,76,70,0.2)]',
          'hover:[&_input]:border-[#3e3e3e]',

          // Date input
          "[&_input[type='date']]:pr-[7px]",

          // Label styles
          '[&_.gigya-label-text]:font-[Graphik,Arial,sans-serif] [&_.gigya-label-text]:text-sm',
          'md:[&_.gigya-label-text]:text-base',

          // Submit button styles
          "[&_input[type='submit']]:!h-12 [&_input[type='submit']]:!border-0 [&_input[type='submit']]:!bg-[#2c3e50] [&_input[type='submit']]:!font-special [&_input[type='submit']]:!text-[9px] [&_input[type='submit']]:!uppercase [&_input[type='submit']]:!tracking-[0.28em] [&_input[type='submit']]:!text-white",
          '[&_.gigya-button-link]:h-12 [&_.gigya-button-link]:border-0 [&_.gigya-button-link]:bg-[#2c3e50] [&_.gigya-button-link]:font-special [&_.gigya-button-link]:text-[9px] [&_.gigya-button-link]:uppercase [&_.gigya-button-link]:tracking-[0.28em] [&_.gigya-button-link]:text-white',
          "hover:[&_.gigya-button-link]:bg-[#3a5066] hover:[&_input[type='submit']]:!bg-[#3a5066]",
          "md:[&_.gigya-button-link]:!text-[11px] md:[&_input[type='submit']]:!text-[11px]",

          // Button link styles
          '[&_.gigya-button-link]:pb-0',
          '[&_.gigya-button-link_input]:border-0 [&_.gigya-button-link_input]:bg-transparent [&_.gigya-button-link_input]:font-special [&_.gigya-button-link_input]:text-[9px] [&_.gigya-button-link_input]:uppercase [&_.gigya-button-link_input]:tracking-[0.28em] [&_.gigya-button-link_input]:text-white',

          // Radio and checkbox styles
          '[&_.gigya-multi-choice-item]:flex [&_.gigya-multi-choice-item]:items-center',
          "[&_input[type='radio']]:h-4 [&_input[type='radio']]:w-4 [&_input[type='radio']]:accent-[#5e4c46] [&_input[type='radio']]:outline-0",
          "[&_input[type='checkbox']]:h-4 [&_input[type='checkbox']]:w-4 [&_input[type='checkbox']]:accent-[#5e4c46] [&_input[type='checkbox']]:outline-0",
          "[&_input[type='checkbox']:focus]:shadow-none [&_input[type='radio']:focus]:shadow-none",
          "[&_input[type='radio']+label]:font-[Graphik,Arial,sans-serif] [&_input[type='radio']+label]:text-sm",
          "[&_input[type='checkbox']+label]:font-[Graphik,Arial,sans-serif] [&_input[type='checkbox']+label]:text-sm",
          "md:[&_input[type='checkbox']+label]:text-base md:[&_input[type='radio']+label]:text-base",

          // Screen set specific styles
          "[&[data-screen-set='Gassan-RegistrationLogin']]:w-full [&[data-screen-set='Gassan-RegistrationLogin']]:p-0",

          // Lite registration styles
          "[&[data-screen-set='Gassan-LiteRegistration']_.gigya-screen]:w-full [&[data-screen-set='Gassan-LiteRegistration']_.gigya-screen]:p-0",
          "[&[data-screen-set='Gassan-LiteRegistration']_.gigya-screen_input[type='checkbox']:focus]:shadow-none",
          "[&[data-screen-set='Gassan-LiteRegistration']_.gigya-screen_p]:font-[Graphik,Arial,sans-serif] [&[data-screen-set='Gassan-LiteRegistration']_.gigya-screen_p]:text-sm [&[data-screen-set='Gassan-LiteRegistration']_.gigya-screen_p]:leading-[1.6]",
          "md:[&[data-screen-set='Gassan-LiteRegistration']_.gigya-screen_p]:text-base",

          // Register screen styles
          "[&[data-start-screen='gigya-register-screen-form-only']_.gigya-screen-content>div:first-of-type]:w-full [&[data-start-screen='gigya-register-screen-form-only']_.gigya-screen-content>div:first-of-type]:p-0",
          "[&[data-start-screen='gigya-register-screen-form-only']_.gigya-screen-content>div:first-of-type_input[type='checkbox']:focus]:shadow-none",
          "[&[data-start-screen='gigya-register-screen-form-only']_h2]:font-[Graphik,Arial,sans-serif] [&[data-start-screen='gigya-register-screen-form-only']_h2]:text-sm [&[data-start-screen='gigya-register-screen-form-only']_h2]:leading-[1.6]",
          "md:[&[data-start-screen='gigya-register-screen-form-only']_h2]:text-base",

          // Forgot password screen styles
          "[&[data-start-screen='gigya-forgot-password-screen']_.gigya-screen]:w-full [&[data-start-screen='gigya-forgot-password-screen']_.gigya-screen]:p-0",
          "[&[data-start-screen='gigya-forgot-password-screen']_.gigya-screen_.main-text]:font-[Graphik,Arial,sans-serif] [&[data-start-screen='gigya-forgot-password-screen']_.gigya-screen_.main-text]:text-sm",
          "md:[&[data-start-screen='gigya-forgot-password-screen']_.gigya-screen_.main-text]:text-base",

          // Social login buttons styles
          "[&[data-start-screen='gigya-social-login-buttons']_.gigya-screen.portrait_.gigya-composite-control.gigya-composite-control-social-login]:right-0 [&[data-start-screen='gigya-social-login-buttons']_.gigya-screen.portrait_.gigya-composite-control.gigya-composite-control-social-login]:h-auto [&[data-start-screen='gigya-social-login-buttons']_.gigya-screen.portrait_.gigya-composite-control.gigya-composite-control-social-login]:w-full [&[data-start-screen='gigya-social-login-buttons']_.gigya-screen.portrait_.gigya-composite-control.gigya-composite-control-social-login]:p-0",
          "[&[data-start-screen='gigya-social-login-buttons']_.gigya-screen]:right-0 [&[data-start-screen='gigya-social-login-buttons']_.gigya-screen]:h-auto [&[data-start-screen='gigya-social-login-buttons']_.gigya-screen]:w-full [&[data-start-screen='gigya-social-login-buttons']_.gigya-screen]:p-0",
          "[&[data-start-screen='gigya-social-login-buttons']_.gigya-social-login-container]:right-0 [&[data-start-screen='gigya-social-login-buttons']_.gigya-social-login-container]:h-auto [&[data-start-screen='gigya-social-login-buttons']_.gigya-social-login-container]:w-full [&[data-start-screen='gigya-social-login-buttons']_.gigya-social-login-container]:p-0",
          "[&[data-start-screen='gigya-social-login-buttons']_.gigya-social-login-container>div:first-of-type]:h-auto [&[data-start-screen='gigya-social-login-buttons']_.gigya-social-login-container>div:first-of-type]:w-full [&[data-start-screen='gigya-social-login-buttons']_.gigya-social-login-container>div:first-of-type]:p-0",
          "[&[data-start-screen='gigya-social-login-buttons']_.gigya-login-providers]:h-[45px]",
          "[&[data-start-screen='gigya-social-login-buttons']_table]:block [&[data-start-screen='gigya-social-login-buttons']_table]:w-full",
          "[&[data-start-screen='gigya-social-login-buttons']_tbody]:block [&[data-start-screen='gigya-social-login-buttons']_tbody]:w-full",
          "[&[data-start-screen='gigya-social-login-buttons']_tr]:block [&[data-start-screen='gigya-social-login-buttons']_tr]:w-full",
          "[&[data-start-screen='gigya-social-login-buttons']_td]:block [&[data-start-screen='gigya-social-login-buttons']_td]:w-full",
          "[&[data-start-screen='gigya-social-login-buttons']_.gigya-login-provider-row]:grid [&[data-start-screen='gigya-social-login-buttons']_.gigya-login-provider-row]:w-full [&[data-start-screen='gigya-social-login-buttons']_.gigya-login-provider-row]:grid-cols-3 [&[data-start-screen='gigya-social-login-buttons']_.gigya-login-provider-row]:gap-2 [&[data-start-screen='gigya-social-login-buttons']_.gigya-login-provider-row]:p-0",
          "[&[data-start-screen='gigya-social-login-buttons']_.gigya-login-provider-row_span.gigya-login-provider]:mr-0 [&[data-start-screen='gigya-social-login-buttons']_.gigya-login-provider-row_span.gigya-login-provider]:w-full",

          // Reset password screen styles
          "[&[data-start-screen='gigya-reset-password-screen']_.gigya-screen]:w-full [&[data-start-screen='gigya-reset-password-screen']_.gigya-screen]:p-0",
        )}
        data-start-screen={startScreen}
        data-screen-set={screenSet}
      >
        <div id={id}>
          {!isReady && (
            <div>
              <Spinner size={24} />
            </div>
          )}
        </div>
      </div>
    </>
  )
}
